import React from 'react';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { FormattedMessage } from 'react-intl';

import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';

import ErrorMessage from '../components/error-message';
import SubmitButton from '../components/submit-button';
import { TextField } from '../components/fields';
import { TitleFormattedMessage } from '../components/titleFormattedMessage';

interface ChangeEmailFormProps {
  errors: { [key: string]: any };
  isSubmitting: boolean;
  isValid: boolean;
  dirty: boolean;
  currentEmail: string;
  onCancel: (...args: any[]) => any;
}

const ChangeEmailForm = ({ errors, isSubmitting, isValid, dirty, currentEmail, onCancel }: ChangeEmailFormProps) => {
  return (
    <Form noValidate>
      {errors._general && (
        <Alert variant="danger">
          <ErrorMessage>{errors._general}</ErrorMessage>
        </Alert>
      )}

      <div>
        <div className="mb-3">
          <label className="form-label">
            <FormattedMessage id="form.email.current" defaultMessage="Current Email" />
          </label>
          <p className="form-control-plaintext">{currentEmail}</p>
        </div>

        <TextField
          id="changeEmailForm.email"
          name="email"
          type="email"
          label={<FormattedMessage id="form.email.new" defaultMessage="New Email" />}
          data-testid="new-email-field"
        />

        <TextField
          id="changeEmailForm.confirmEmail"
          name="confirmEmail"
          type="email"
          label={<FormattedMessage id="form.email.confirm" defaultMessage="Confirm New Email" />}
          data-testid="confirm-email-field"
        />

        <TextField
          id="changeEmailForm.currentPassword"
          name="currentPassword"
          type="password"
          label={<FormattedMessage id="form.password.current" defaultMessage="Current Password" />}
          data-testid="current-password-field"
        />
      </div>
      <div className="d-flex justify-content-end">
        {/* @ts-ignore */}
        <Button className="mr-3" variant="link-secondary" onClick={onCancel} data-testid="cancel-email-change">
          <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
        </Button>
        <SubmitButton
          label={<FormattedMessage id="cta.changeEmail" defaultMessage="Change Email" />}
          disabled={isSubmitting || !isValid || !dirty}
          isSubmitting={isSubmitting}
          data-testid="change-email-button"
        />
      </div>
    </Form>
  );
};

const schema = yup.object().shape({
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  confirmEmail: yup
    .string()
    .oneOf([yup.ref('email')], 'Email addresses must match')
    .required('Please confirm your email address'),
  currentPassword: yup
    .string()
    .required('Current password is required'),
});

interface ChangeEmailFormikProps {
  onCancel: (...args: any[]) => any;
  onSave: (values: { email: string; currentPassword: string }) => any;
  currentEmail: string;
}

const ChangeEmailFormik = ({ onCancel, onSave, currentEmail }: ChangeEmailFormikProps) => {
  return (
    <Formik
      validationSchema={schema}
      onSubmit={({ email, currentPassword }) => onSave({ email, currentPassword })}
      component={(formikProps) => <ChangeEmailForm {...formikProps} currentEmail={currentEmail} onCancel={onCancel} />}
      initialValues={{
        email: '',
        confirmEmail: '',
        currentPassword: '',
      }}
    />
  );
};

export default ChangeEmailFormik;
